import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { Box, Typography, TextField, IconButton, CircularProgress, Avatar, Divider } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import { SocketContext } from '../../context/SocketContext';
import { getMiSoporte, enviarMensajeSoporte } from '../../actions/support';
import BotonAdjuntar from '../../components/chat/BotonAdjuntar';
import VistaPreviaAdjuntos from '../../components/chat/VistaPreviaAdjuntos';
import AdjuntosMensaje from '../../components/chat/AdjuntosMensaje';
import { useAdjuntos } from '../../components/chat/useAdjuntos';
import { MAX_TEXTO_SOPORTE } from '../../components/chat/limites';
import { etiquetaDia, horaMensaje, mismoDia } from '../../components/chat/fechas';

const Burbuja = ({ msg }) => {
  const mia = msg.from === 'user';
  return (
    <Box sx={{ display: 'flex', justifyContent: mia ? 'flex-end' : 'flex-start', mb: 1.2 }}>
      <Box sx={{ maxWidth: '78%' }}>
        {!mia && (
          <Typography variant="caption" sx={{ color: 'secondary.main', fontWeight: 700, ml: 1, display: 'block', mb: 0.3 }}>
            Soporte
          </Typography>
        )}
        <Box sx={{
          px: 1.6, py: 1, borderRadius: 2.5,
          bgcolor: mia ? 'primary.main' : 'sutil',
          color: mia ? 'white' : 'primary.main',
          borderTopRightRadius: mia ? 4 : 20,
          borderTopLeftRadius: mia ? 20 : 4,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
          {!msg.soloAdjuntos && <Typography variant="body2">{msg.content}</Typography>}
          <AdjuntosMensaje adjuntos={msg.adjuntos} />
          <Typography variant="caption" sx={{ display: 'block', mt: 0.3, opacity: 0.7, textAlign: 'right', fontSize: '0.65rem' }}>
            {horaMensaje(msg.createdAt)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const SoporteChat = () => {
  const socket = useContext(SocketContext);
  const [mensajes, setMensajes] = useState([]);
  const [texto, setTexto] = useState('');
  const [cargando, setCargando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const adj = useAdjuntos();
  const finRef = useRef(null);

  const scrollAbajo = () => {
    requestAnimationFrame(() => finRef.current?.scrollIntoView({ behavior: 'smooth' }));
  };

  useEffect(() => {
    let vivo = true;
    getMiSoporte().then((r) => {
      if (!vivo) return;
      if (r.ok) setMensajes(r.chat.messages || []);
      setCargando(false);
      scrollAbajo();
    });
    return () => { vivo = false; };
  }, []);

  // Respuestas del admin en vivo (llegan al room del propio usuario)
  useEffect(() => {
    if (!socket) return;
    const onMsg = (data) => {
      if (data?.from === 'admin' && data.message) {
        setMensajes((prev) => [...prev, data.message]);
        scrollAbajo();
      }
    };
    socket.on('support_message', onMsg);
    return () => { socket.off('support_message', onMsg); };
  }, [socket]);

  const enviar = async () => {
    const content = texto.trim();
    const archivos = adj.archivos;
    if ((!content && archivos.length === 0) || enviando || adj.procesando) return;
    setEnviando(true);
    setTexto('');
    const r = await enviarMensajeSoporte(content, archivos);
    setEnviando(false);
    if (r.ok) {
      adj.limpiar();
      setMensajes((prev) => [...prev, r.message]);
      scrollAbajo();
    } else {
      setTexto((actual) => actual || content); // no se envió: devolvemos el texto y conservamos los archivos
      adj.setError(r.error);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); }
  };

  // Referencia para "Hoy" / "Ayer" en los separadores de la conversación
  const ahora = new Date();

  return (
    <Box className="main-page" sx={{ pb: 6 }}>
      <Box sx={{ bgcolor: 'secondary.main', color: 'primary.main', borderRadius: 2, px: 4, py: 4, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'primary.main' }}>
          <SupportAgentOutlinedIcon />
        </Avatar>
        <Box>
          <Typography variant="h5">Chat con soporte</Typography>
          <Typography variant="body2">
            <small>Escríbenos y el equipo de OneService te responderá aquí mismo.</small>
          </Typography>
        </Box>
      </Box>

      <Box sx={{ border: 1, borderColor: 'sutil', borderRadius: 3, bgcolor: 'white', display: 'flex', flexDirection: 'column', height: { xs: '60vh', md: '62vh' } }}>
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {cargando ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={26} sx={{ color: 'secondary.main' }} />
            </Box>
          ) : mensajes.length === 0 ? (
            <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 6, px: 2 }}>
              <SupportAgentOutlinedIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="body2">
                ¿Necesitas ayuda? Escribe tu primer mensaje y con gusto te atendemos.
              </Typography>
            </Box>
          ) : (
            mensajes.map((m, i) => {
              // El primer mensaje de cada día lleva arriba el día en que se mandó ("Hoy", "Ayer", "9 de octubre"…)
              const dia = i === 0 || !mismoDia(mensajes[i - 1].createdAt, m.createdAt) ? etiquetaDia(m.createdAt, ahora) : '';
              return (
                <Fragment key={m._id || i}>
                  {dia && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: i === 0 ? 0 : 2, mb: 1.5 }}>
                      <Divider sx={{ flex: 1 }} />
                      <Typography variant="caption" sx={{ px: 1.5, color: 'text.secondary', fontWeight: 600 }}>{dia}</Typography>
                      <Divider sx={{ flex: 1 }} />
                    </Box>
                  )}
                  <Burbuja msg={m} />
                </Fragment>
              );
            })
          )}
          <div ref={finRef} />
        </Box>

        <Box sx={{ borderTop: 1, borderColor: 'sutil', p: 1.5 }}>
          <VistaPreviaAdjuntos archivos={adj.archivos} error={adj.error} onQuitar={adj.quitar} enviando={enviando} procesando={adj.procesando} />
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
            <BotonAdjuntar onArchivos={adj.agregar} disabled={enviando} />
            <TextField
              fullWidth
              size="small"
              multiline
              maxRows={4}
              placeholder="Escribe un mensaje…"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={onKeyDown}
              inputProps={{ maxLength: MAX_TEXTO_SOPORTE }}
            />
            <IconButton
              color="primary"
              onClick={enviar}
              aria-label="Enviar mensaje"
              disabled={(!texto.trim() && adj.archivos.length === 0) || enviando || adj.procesando}
              sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'grey.400', color: 'white' } }}
            >
              {enviando ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SoporteChat;
